import {
  forceCenter,
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum
} from "d3-force";
import type { CognitiveGraph, CognitiveGraphEdge, CognitiveGraphNode } from "../api/types";
import { store } from "../state";

interface GraphNode extends SimulationNodeDatum, CognitiveGraphNode {
  /** Hemisferio: -1 pessoal, +1 compartilhado. */
  side: number;
  highlighted: boolean;
  /**
   * Evidencia e desenhada com forma propria e nunca vira entidade. Promover memoria a no de
   * primeira classe deixaria o grafo denso e sem significado: cada entidade se ligaria a cada
   * memoria que a menciona, e a estrutura que a tela existe para mostrar desapareceria no meio.
   */
  kind: "entity" | "evidence";
}

interface GraphLink extends SimulationLinkDatum<GraphNode> {
  edge: CognitiveGraphEdge | null;
  highlighted: boolean;
  kind: "relation" | "evidence";
}

const PERSONAL_SCOPES = new Set(["User"]);
/** Movimento ate aqui ainda conta como toque, nao como arrasto. O dedo nunca fica parado. */
const TAP_TOLERANCE_PX = 8;
const DOUBLE_TAP_MS = 320;
const MIN_SCALE = 0.3;
const MAX_SCALE = 3;
/** Calor mantido na simulacao durante o arrasto: alto o bastante para os vizinhos reagirem. */
const NODE_DRAG_ALPHA = 0.3;

/**
 * Grafo Cognitivo em canvas 2D com simulacao de forcas.
 *
 * A silhueta de dois hemisferios nao e decoracao: os grupos sao ESCOPO — conhecimento pessoal de
 * um lado, organizacional/global do outro. Uma separacao visual que nao significasse nada seria
 * ruido bonito.
 *
 * Regra que nao se quebra: nenhuma aresta e desenhada sem existir no read model. Um grafo
 * decorativo destruiria justamente o valor de auditoria da tela.
 */
export class GraphCanvas {
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;
  private readonly overlay: HTMLElement;
  private simulation: Simulation<GraphNode, GraphLink> | null = null;
  private nodes: GraphNode[] = [];
  private links: GraphLink[] = [];
  /**
   * A referencia e guardada de proposito: um ResizeObserver sem referencia viva pode ser coletado
   * pelo GC e para de disparar em silencio. Foi o que deixou o canvas preso na primeira medida,
   * feita antes de o layout existir.
   */
  private readonly resizeObserver: ResizeObserver;
  private lastSignature = "";
  private transform = { x: 0, y: 0, scale: 1 };
  private dragging: { startX: number; startY: number; originX: number; originY: number } | null = null;
  private hoveredKey: string | null = null;
  /** Ponteiros ativos. Dois simultaneos viram pinca; um so, arrasto. */
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinch: { distance: number; scale: number } | null = null;
  /** No sob o ponteiro no momento em que ele encostou. Decide se o gesto move o no ou a tela. */
  private grabbed: GraphNode | null = null;
  private draggedNode: GraphNode | null = null;
  private lastTap: { key: string; at: number } | null = null;
  private readonly coarsePointer = window.matchMedia("(pointer: coarse)").matches;

  constructor(
    private readonly root: HTMLElement,
    private readonly onSelect: (key: string | null) => void,
    private readonly onFocus: (key: string) => void
  ) {
    this.root.innerHTML = `
      <canvas id="graph-canvas"></canvas>
      <div class="graph-overlay" id="graph-overlay"></div>
      <div class="graph-legend">
        <span><i class="dot registered"></i> registrado</span>
        <span><i class="dot synthetic"></i> derivado</span>
        <span><i class="dot used"></i> usado na resposta</span>
        <span><i class="dot evidence"></i> evidência</span>
      </div>
    `;

    this.canvas = this.root.querySelector<HTMLCanvasElement>("#graph-canvas")!;
    this.context = this.canvas.getContext("2d")!;
    this.overlay = this.root.querySelector<HTMLElement>("#graph-overlay")!;

    this.bindPointerEvents();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.root);
    this.resize();

    store.subscribe((state) => {
      // Reconstruir a cada atualizacao da store reiniciaria a simulacao a cada tecla digitada no
      // chat, e o grafo ficaria em movimento perpetuo. So o que muda a topologia reconstroi.
      const signature = [
        state.graph?.lastUpdated ?? "",
        state.graph?.nodes.length ?? 0,
        state.graph?.edges.length ?? 0,
        state.graph?.scope.focus ?? "",
        state.lastResponse?.assistantMessageId ?? "",
        state.selectedNodeKey ?? "",
        state.evidenceFor ?? "",
        state.evidence.map((memory) => memory.id).join(","),
        state.graphLoading,
        state.graphError ?? ""
      ].join("|");
      if (signature === this.lastSignature) {
        return;
      }
      this.lastSignature = signature;
      this.sync(state.graph, state.graphLoading, state.graphError);
    });
  }

  private bindPointerEvents(): void {
    // Sem isto o navegador rola a pagina e aplica o proprio zoom enquanto se arrasta o grafo:
    // o gesto vira briga entre a pagina e o canvas, e nenhum dos dois responde direito.
    this.canvas.style.touchAction = "none";

    this.canvas.addEventListener("pointerdown", (event) => {
      this.pointers.set(event.pointerId, { x: event.offsetX, y: event.offsetY });
      this.capturePointer(event.pointerId);

      if (this.pointers.size === 2) {
        this.pinch = { distance: this.pointerDistance(), scale: this.transform.scale };
        this.dragging = null;
        return;
      }

      // A selecao acontece no LEVANTAR, nao no encostar: no toque, arrastar o grafo comeca em
      // cima de algum no na maioria das vezes, e selecionar ali abriria o painel a cada
      // tentativa de mover a tela.
      //
      // O que estava sob o ponteiro decide o gesto: sobre um no, mover arrasta o NO; sobre o
      // vazio, mover desloca a tela. A decisao so e tomada quando o movimento passa a tolerancia
      // de toque, senao um clique tremido viraria arrasto.
      this.grabbed = this.nodeAt(event.offsetX, event.offsetY);
      this.dragging = {
        startX: event.offsetX,
        startY: event.offsetY,
        originX: this.transform.x,
        originY: this.transform.y
      };
    });

    this.canvas.addEventListener("pointermove", (event) => {
      if (this.pointers.has(event.pointerId)) {
        this.pointers.set(event.pointerId, { x: event.offsetX, y: event.offsetY });
      }

      if (this.pinch && this.pointers.size >= 2) {
        this.applyPinch();
        return;
      }

      if (this.draggedNode) {
        this.moveDraggedNode(event.offsetX, event.offsetY);
        return;
      }

      if (this.dragging) {
        const moved = Math.hypot(
          event.offsetX - this.dragging.startX,
          event.offsetY - this.dragging.startY
        );

        // Passou da tolerancia com um no sob o dedo: comeca a arrastar o no.
        if (this.grabbed && moved > TAP_TOLERANCE_PX) {
          this.startNodeDrag(this.grabbed, event.offsetX, event.offsetY);
          return;
        }

        // Sobre o vazio o gesto desloca a tela. Sobre um no, abaixo da tolerancia, nada acontece
        // ainda — o gesto ainda pode virar um toque.
        if (!this.grabbed) {
          this.transform.x = this.dragging.originX + (event.offsetX - this.dragging.startX);
          this.transform.y = this.dragging.originY + (event.offsetY - this.dragging.startY);
          this.draw();
        }
        return;
      }

      const hovered = this.nodeAt(event.offsetX, event.offsetY);
      const hoveredKey = hovered?.key ?? null;
      if (hoveredKey !== this.hoveredKey) {
        this.hoveredKey = hoveredKey;
        this.canvas.style.cursor = hovered ? "pointer" : "grab";
        this.draw();
      }
    });

    const endPointer = (event: PointerEvent) => {
      const wasDragging = this.dragging;
      const wasDraggingNode = this.draggedNode !== null;
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) {
        this.pinch = null;
      }
      this.releasePointer(event.pointerId);
      this.dragging = null;
      this.grabbed = null;
      this.endNodeDrag();

      // Arrastar um no nao seleciona: quem moveu queria mover.
      if (event.type !== "pointerup" || !wasDragging || wasDraggingNode) {
        return;
      }

      const moved = Math.hypot(event.offsetX - wasDragging.startX, event.offsetY - wasDragging.startY);
      if (moved > TAP_TOLERANCE_PX) {
        return;
      }

      const node = this.nodeAt(event.offsetX, event.offsetY);
      // Evidencia nao e entidade: tocar nela nao troca a selecao, senao o painel perderia o
      // no que a originou e os satelites sumiriam junto.
      if (!node || node.kind === "evidence") {
        return;
      }

      // Duplo toque explora a vizinhanca. O dblclick do mouse nao chega no toque, entao a
      // deteccao e feita aqui — sem ela, explorar o grafo so existiria no desktop.
      const now = performance.now();
      const isDoubleTap = this.lastTap !== null &&
        this.lastTap.key === node.key &&
        now - this.lastTap.at < DOUBLE_TAP_MS;
      this.lastTap = isDoubleTap ? null : { key: node.key, at: now };

      if (isDoubleTap) {
        this.onFocus(node.key);
      } else {
        this.onSelect(node.key);
      }
    };

    this.canvas.addEventListener("pointerup", endPointer);
    this.canvas.addEventListener("pointercancel", endPointer);

    this.canvas.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        const factor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
        const next = clamp(this.transform.scale * factor, MIN_SCALE, MAX_SCALE);
        // Zoom ancorado no cursor: o que estava sob o ponteiro continua sob o ponteiro.
        const ratio = next / this.transform.scale;
        this.transform.x = event.offsetX - (event.offsetX - this.transform.x) * ratio;
        this.transform.y = event.offsetY - (event.offsetY - this.transform.y) * ratio;
        this.transform.scale = next;
        this.draw();
      },
      { passive: false }
    );
  }

  /**
   * Forca a remedicao do canvas. Chamado ao trazer o grafo de volta no celular: enquanto a aba
   * estava oculta o painel media zero, e o canvas guarda as dimensoes como estilo inline, que
   * vencem o CSS. Depender so do ResizeObserver aqui e fragil — a entrega dele esta amarrada ao
   * ciclo de renderizacao, e o momento em que ela chega varia entre navegadores.
   */
  invalidateSize(): void {
    this.resize();
  }

  /**
   * A captura de ponteiro e conveniencia — mantem o arrasto vivo se o dedo sair do canvas — e
   * nao pode derrubar a interacao. Ela lanca quando o id nao corresponde a um ponteiro ativo, e
   * como e a primeira linha do pointerdown, uma excecao ali deixava o grafo inteiro sem resposta:
   * o arrasto nunca era armado e nenhum toque chegava a selecionar.
   */
  private capturePointer(pointerId: number): void {
    try {
      this.canvas.setPointerCapture(pointerId);
    } catch {
      // Sem captura o gesto ainda funciona enquanto o ponteiro estiver sobre o canvas.
    }
  }

  private releasePointer(pointerId: number): void {
    try {
      this.canvas.releasePointerCapture(pointerId);
    } catch {
      // Ja liberado ou nunca capturado.
    }
  }

  /**
   * Prende o no ao ponteiro e reaquece a simulacao.
   *
   * `fx`/`fy` sao a posicao FIXA que o d3 respeita: enquanto estiverem definidas, o no obedece
   * ao dedo e as forcas agem sobre todo o resto. E dai que vem a sensacao de elastico — os
   * vizinhos sao puxados pelas arestas em vez de o no sair sozinho da estrutura.
   *
   * O `alphaTarget` acima de zero mantem a simulacao viva enquanto durar o gesto. Sem ele o
   * arrasto so funcionaria nos primeiros segundos apos carregar o grafo, porque a simulacao
   * esfria e para de ticar — e o no seguiria o dedo com o resto do grafo congelado.
   */
  private startNodeDrag(node: GraphNode, screenX: number, screenY: number): void {
    this.draggedNode = node;
    this.canvas.style.cursor = "grabbing";
    this.simulation?.alphaTarget(NODE_DRAG_ALPHA).restart();
    this.moveDraggedNode(screenX, screenY);
  }

  private moveDraggedNode(screenX: number, screenY: number): void {
    if (!this.draggedNode) {
      return;
    }

    this.draggedNode.fx = (screenX - this.transform.x) / this.transform.scale;
    this.draggedNode.fy = (screenY - this.transform.y) / this.transform.scale;
    this.draw();
  }

  /**
   * Solta o no. Limpar `fx`/`fy` devolve o controle as forcas: ele nao fica preso onde foi
   * largado, e o grafo se reacomoda ao redor da nova posicao ate parar.
   */
  private endNodeDrag(): void {
    if (!this.draggedNode) {
      return;
    }

    this.draggedNode.fx = null;
    this.draggedNode.fy = null;
    this.draggedNode = null;
    this.canvas.style.cursor = "grab";
    // De volta a zero, a simulacao volta a esfriar e o grafo assenta em vez de tremer para sempre.
    this.simulation?.alphaTarget(0);
  }

  private pointerDistance(): number {
    const [first, second] = [...this.pointers.values()];
    return Math.hypot(first.x - second.x, first.y - second.y);
  }

  /**
   * Pinca ancorada no ponto medio entre os dedos: o que estava entre eles continua entre eles.
   * Sem a ancora, o grafo escapa da tela ao ampliar e o gesto fica impossivel de controlar.
   */
  private applyPinch(): void {
    if (!this.pinch) {
      return;
    }

    const distance = this.pointerDistance();
    if (distance === 0) {
      return;
    }

    const next = clamp(this.pinch.scale * (distance / this.pinch.distance), MIN_SCALE, MAX_SCALE);
    const [first, second] = [...this.pointers.values()];
    const midX = (first.x + second.x) / 2;
    const midY = (first.y + second.y) / 2;
    const ratio = next / this.transform.scale;
    this.transform.x = midX - (midX - this.transform.x) * ratio;
    this.transform.y = midY - (midY - this.transform.y) * ratio;
    this.transform.scale = next;
    this.draw();
  }

  private sync(graph: CognitiveGraph | null, loading: boolean, error: string | null): void {
    this.renderOverlay(graph, loading, error);
    if (!graph) {
      this.nodes = [];
      this.links = [];
      this.simulation?.stop();
      this.draw();
      return;
    }

    const highlightedMemoryIds = this.collectHighlightedMemoryIds();
    const previous = new Map(this.nodes.map((node) => [node.key, node]));

    this.nodes = graph.nodes.map((node) => {
      const carried = previous.get(node.key);
      return {
        ...node,
        // Posicao preservada entre atualizacoes: sem isso o grafo "salta" a cada turno e o
        // usuario perde a referencia espacial que acabou de construir.
        x: carried?.x,
        y: carried?.y,
        vx: carried?.vx,
        vy: carried?.vy,
        // A fixacao tambem atravessa a reconstrucao: um turno que termina no meio de um arrasto
        // nao pode fazer o no escapar do dedo.
        fx: carried?.fx,
        fy: carried?.fy,
        side: PERSONAL_SCOPES.has(node.scope ?? "User") ? -1 : 1,
        highlighted: false,
        kind: "entity" as const
      };
    });

    const byKey = new Map(this.nodes.map((node) => [node.key, node]));

    // A reconstrucao troca os objetos dos nos. Sem reapontar, o arrasto em curso continuaria
    // movendo um no descartado — o dedo mexeria em nada, silenciosamente.
    if (this.draggedNode) {
      this.draggedNode = byKey.get(this.draggedNode.key) ?? null;
    }
    if (this.grabbed) {
      this.grabbed = byKey.get(this.grabbed.key) ?? null;
    }
    this.links = graph.edges
      .filter((edge) => byKey.has(edge.from) && byKey.has(edge.to))
      .map((edge) => {
        const highlighted = edge.sourceMemoryIds.some((id) => highlightedMemoryIds.has(id));
        if (highlighted) {
          byKey.get(edge.from)!.highlighted = true;
          byKey.get(edge.to)!.highlighted = true;
        }
        return {
          source: byKey.get(edge.from)!,
          target: byKey.get(edge.to)!,
          edge,
          highlighted,
          kind: "relation" as const
        };
      });

    this.attachEvidenceSatellites(byKey);
    this.runSimulation();
  }

  /**
   * Camada de evidencia sob demanda: as memorias que sustentam o no selecionado aparecem como
   * satelites temporarios e somem ao desmarcar. E o drill-down dentro do grafo sem o custo de
   * tornar memoria um no permanente — o grafo base continua sendo entidades e relacoes.
   */
  private attachEvidenceSatellites(byKey: Map<string, GraphNode>): void {
    const { selectedNodeKey, evidence, evidenceFor } = store.state;
    // A evidencia so e desenhada quando pertence ao no selecionado agora: uma carga que chegou
    // atrasada, de uma selecao anterior, e simplesmente ignorada.
    if (!selectedNodeKey || evidenceFor !== selectedNodeKey || evidence.length === 0) {
      return;
    }

    const anchor = byKey.get(selectedNodeKey);
    if (!anchor) {
      return;
    }

    for (const memory of evidence) {
      const satellite: GraphNode = {
        key: `evidence:${memory.id}`,
        displayName: memory.title,
        entityType: null,
        scope: memory.scope,
        usageCount: 0,
        degree: 1,
        registered: true,
        sourceMemoryIds: [memory.id],
        // Nasce junto do ancora para que a simulacao o empurre para fora em vez de atravessar
        // o grafo inteiro ate encontrar seu lugar.
        x: anchor.x,
        y: anchor.y,
        side: anchor.side,
        highlighted: false,
        kind: "evidence"
      };
      this.nodes.push(satellite);
      this.links.push({ source: anchor, target: satellite, edge: null, highlighted: false, kind: "evidence" });
    }
  }

  /**
   * As memorias que sustentaram a ultima resposta. E o que separa "a ARTI sabe isto" de "a ARTI
   * consultou isto para responder" — dois estados que a tela precisa mostrar diferentes.
   */
  private collectHighlightedMemoryIds(): Set<string> {
    const response = store.state.lastResponse;
    if (!response) {
      return new Set();
    }
    return new Set([...(response.memorySourceIds ?? []), ...(response.knowledgeSourceIds ?? [])]);
  }

  private runSimulation(): void {
    this.simulation?.stop();
    const width = this.canvas.clientWidth || this.root.clientWidth;
    const height = this.canvas.clientHeight || this.root.clientHeight;
    const separation = Math.min(width * 0.22, 260);

    this.simulation = forceSimulation<GraphNode, GraphLink>(this.nodes)
      .force(
        "link",
        forceLink<GraphNode, GraphLink>(this.links)
          .id((node) => node.key)
          // Satelites orbitam perto do ancora; relacoes ficam na distancia normal.
          .distance((link) => (link.kind === "evidence" ? 46 : 90))
          .strength((link) => (link.kind === "evidence" ? 0.6 : 0.35))
      )
      .force("charge", forceManyBody<GraphNode>().strength(-220))
      .force("collide", forceCollide<GraphNode>((node) => this.radiusOf(node) + 6))
      .force("center", forceCenter(width / 2, height / 2))
      .force("hemisphere", forceX<GraphNode>((node) => width / 2 + node.side * separation).strength(0.09))
      .force("vertical", forceY<GraphNode>(height / 2).strength(0.05))
      .alpha(0.9)
      .on("tick", () => this.draw());

    // A simulacao e recriada a cada atualizacao do grafo. Se havia um arrasto em curso, o calor
    // precisa ser reposto na instancia nova, senao o no seguiria o dedo com o resto congelado.
    if (this.draggedNode) {
      this.simulation.alphaTarget(NODE_DRAG_ALPHA);
    }
  }

  private renderOverlay(graph: CognitiveGraph | null, loading: boolean, error: string | null): void {
    if (loading && !graph) {
      this.overlay.innerHTML = `<div class="graph-note">Carregando o grafo…</div>`;
      return;
    }
    if (error) {
      this.overlay.innerHTML = `<div class="graph-note error">${error}</div>`;
      return;
    }
    if (!graph || graph.nodes.length === 0) {
      this.overlay.innerHTML = `
        <div class="graph-note">
          <strong>Nenhum conhecimento aprovado ainda.</strong>
          <span>Ensine algo na conversa e aprove a sugestão: o nó aparece aqui.</span>
        </div>`;
      return;
    }

    const scope = graph.scope;
    const parts: string[] = [];
    if (scope.focus) {
      parts.push(`foco <strong>${scope.focus}</strong> · ${scope.depth} salto${scope.depth === 1 ? "" : "s"}`);
    }
    parts.push(`${graph.nodes.length}/${scope.totalNodes} nós · ${graph.edges.length}/${scope.totalEdges} arestas`);
    // Truncamento sempre declarado pelo servidor, nunca deduzido do tamanho da lista.
    const truncated = scope.truncated ? `<span class="graph-truncated">recorte</span>` : "";
    const updated = graph.lastUpdated
      ? `<span class="graph-updated" title="${graph.lastUpdated}">atualizado ${formatRelative(graph.lastUpdated)}</span>`
      : "";
    this.overlay.innerHTML = `<div class="graph-status">${parts.join(" · ")} ${truncated} ${updated}</div>`;
  }

  private radiusOf(node: GraphNode): number {
    // Evidencia tem tamanho fixo e pequeno: ela nao compete em importancia com as entidades.
    return node.kind === "evidence"
      ? 5
      : 6 + Math.min(14, Math.sqrt(node.degree + node.usageCount) * 3);
  }

  private nodeAt(screenX: number, screenY: number): GraphNode | null {
    const x = (screenX - this.transform.x) / this.transform.scale;
    const y = (screenY - this.transform.y) / this.transform.scale;
    // A folga cresce no toque: o dedo cobre bem mais que o ponteiro, e uma area de acerto
    // pensada para o mouse torna os nos pequenos praticamente inalcancaveis no celular. Ela e
    // dividida pela escala para que a tolerancia continue valendo em pixels de tela, e nao
    // encolha junto com o grafo quando o usuario afasta o zoom.
    const slack = (this.coarsePointer ? 14 : 4) / this.transform.scale;
    for (let index = this.nodes.length - 1; index >= 0; index--) {
      const node = this.nodes[index];
      if (node.x === undefined || node.y === undefined) {
        continue;
      }
      const radius = this.radiusOf(node) + slack;
      if ((node.x - x) ** 2 + (node.y - y) ** 2 <= radius ** 2) {
        return node;
      }
    }
    return null;
  }

  private resize(): void {
    const ratio = window.devicePixelRatio || 1;
    const width = this.root.clientWidth;
    const height = this.root.clientHeight;
    if (width === 0 || height === 0) {
      // Medida antes de o layout existir. Fixar zero aqui congelaria o canvas, porque as
      // dimensoes sao escritas como estilo inline e vencem o CSS.
      requestAnimationFrame(() => this.resize());
      return;
    }

    this.canvas.width = width * ratio;
    this.canvas.height = height * ratio;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.draw();
  }

  private draw(): void {
    const context = this.context;
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    context.clearRect(0, 0, width, height);
    context.save();
    context.translate(this.transform.x, this.transform.y);
    context.scale(this.transform.scale, this.transform.scale);

    const selectedKey = store.state.selectedNodeKey;

    for (const link of this.links) {
      const source = link.source as GraphNode;
      const target = link.target as GraphNode;
      if (source.x === undefined || target.x === undefined) {
        continue;
      }
      const touchesSelection = selectedKey === source.key || selectedKey === target.key;
      context.beginPath();
      context.moveTo(source.x, source.y!);
      context.lineTo(target.x, target.y!);
      if (link.kind === "evidence") {
        // Tracejado fino: a ligacao com a evidencia nao e uma relacao do grafo e nao pode ser
        // confundida com uma.
        context.strokeStyle = "rgba(150, 165, 200, 0.35)";
        context.lineWidth = 1;
        context.setLineDash([2, 4]);
        context.stroke();
        context.setLineDash([]);
        continue;
      }

      context.strokeStyle = link.highlighted
        ? "rgba(126, 231, 195, 0.85)"
        : touchesSelection
          ? "rgba(150, 170, 210, 0.65)"
          : "rgba(120, 135, 170, 0.22)";
      context.lineWidth = link.highlighted ? 2 : touchesSelection ? 1.6 : 1;
      context.stroke();
    }

    for (const node of this.nodes) {
      if (node.x === undefined || node.y === undefined) {
        continue;
      }
      const radius = this.radiusOf(node);
      const isSelected = node.key === selectedKey;
      const isHovered = node.key === this.hoveredKey;

      if (node.kind === "evidence") {
        // Losango, nao circulo: evidencia e outra categoria de coisa, e a forma diz isso antes
        // da cor. O rotulo so aparece sob o cursor, senao os satelites poluem a leitura.
        context.beginPath();
        context.moveTo(node.x, node.y - radius);
        context.lineTo(node.x + radius, node.y);
        context.lineTo(node.x, node.y + radius);
        context.lineTo(node.x - radius, node.y);
        context.closePath();
        context.fillStyle = isHovered ? "rgba(226, 232, 245, 0.9)" : "rgba(150, 165, 200, 0.55)";
        context.fill();
        if (isHovered) {
          context.font = "500 11px Manrope, system-ui, sans-serif";
          context.fillStyle = "rgba(226, 232, 245, 0.92)";
          context.textAlign = "center";
          context.fillText(node.displayName, node.x, node.y + radius + 13);
        }
        continue;
      }

      if (node.highlighted) {
        context.beginPath();
        context.arc(node.x, node.y, radius + 7, 0, Math.PI * 2);
        context.fillStyle = "rgba(126, 231, 195, 0.16)";
        context.fill();
      }

      context.beginPath();
      context.arc(node.x, node.y, radius, 0, Math.PI * 2);
      if (node.registered) {
        context.fillStyle = node.highlighted ? "#7ee7c3" : node.side < 0 ? "#7aa2f7" : "#c39bf5";
        context.fill();
      } else {
        // No derivado de uma ponta de aresta: contorno, nunca preenchido. A UI nao deve
        // apresenta-lo como entidade curada.
        context.fillStyle = "rgba(18, 22, 34, 0.9)";
        context.fill();
        context.strokeStyle = node.highlighted ? "#7ee7c3" : "rgba(160, 175, 210, 0.75)";
        context.lineWidth = 1.5;
        context.setLineDash([3, 3]);
        context.stroke();
        context.setLineDash([]);
      }

      if (isSelected || isHovered) {
        context.beginPath();
        context.arc(node.x, node.y, radius + 4, 0, Math.PI * 2);
        context.strokeStyle = isSelected ? "#ffffff" : "rgba(255,255,255,0.45)";
        context.lineWidth = isSelected ? 2 : 1;
        context.stroke();
      }

      // Nos derivados sao sempre rotulados, independente do tamanho. Eles sinalizam lacuna de
      // curadoria — uma ponta de relacao aprovada que o registro canonico nao conhece — e sao
      // justamente os que o criterio de raio escondia: com 1 relacao e 0 usos o raio da exatos
      // 9px, abaixo do limiar, entao o no mais importante de identificar ficava sem nome.
      if (!node.registered || radius > 9 || isSelected || isHovered || node.highlighted) {
        context.font = "500 12px Manrope, system-ui, sans-serif";
        context.fillStyle = "rgba(226, 232, 245, 0.92)";
        context.textAlign = "center";
        context.fillText(node.displayName, node.x, node.y + radius + 14);
      }
    }

    context.restore();
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function formatRelative(iso: string): string {
  const elapsedMs = Date.now() - new Date(iso).getTime();
  const seconds = Math.round(elapsedMs / 1000);
  if (seconds < 60) {
    return `há ${Math.max(seconds, 0)}s`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `há ${minutes} min`;
  }
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `há ${hours} h` : `há ${Math.round(hours / 24)} d`;
}
